// sheet-range.util (WB-7592) — decodes the absolute A1 range on a `kind: 'cell'` source location.
// Pure, so the arithmetic that can silently land on the wrong row is unit-testable on its own.

import * as XLSX from 'xlsx';

/** Absolute, 0-indexed sheet coordinates of a single-row span. Columns are INCLUSIVE. */
export interface SheetRowSpan {
  row: number;
  colStart: number;
  colEnd: number;
}

/** Validated up front: `decode_range` answers for `'nonsense'`, and a bogus answer highlights a WRONG row. */
const A1_RANGE = /^\$?[A-Za-z]{1,3}\$?\d{1,7}(?::\$?[A-Za-z]{1,3}\$?\d{1,7})?$/;

/**
 * Decode `range`, or `null` when it isn't a usable A1 range. Never throws — a renderer must not
 * break on a payload. Tolerates two things the contract forbids: a multi-row range (clamped to its
 * first row) and a reversed one (`E37:A37`), so odd input degrades instead of vanishing.
 */
export function decodeRowSpan(range: string | null | undefined): SheetRowSpan | null {
  if (typeof range !== 'string') {
    return null;
  }
  const trimmed = range.trim();
  if (!A1_RANGE.test(trimmed)) {
    return null;
  }
  const { s, e } = XLSX.utils.decode_range(trimmed);
  const row = Math.min(s.r, e.r);
  const colStart = Math.min(s.c, e.c);
  const colEnd = Math.max(s.c, e.c);
  if (!Number.isFinite(row) || !Number.isFinite(colStart) || !Number.isFinite(colEnd) || row < 0 || colStart < 0) {
    return null;
  }
  return { row, colStart, colEnd };
}
